import React from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { Link } from 'react-router-dom';

const Feature = ({ icon, title, description }) => (
  <div className="flex gap-3">
    <span className="text-2xl">{icon}</span>
    <div>
      <h3 className="font-semibold text-gray-800">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  </div>
);

const Home = () => {
  const isAuthenticated = useIsAuthenticated();

  return (
    <div className="container mx-auto p-4">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Welcome to <span className="text-teal-600">comet</span> ☄️</h1>
        <p className="text-lg text-gray-600">Cloud Operations Management &amp; Engineering Tool</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <section className="bg-gray-50 p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-3">About comet</h2>
          <p className="text-gray-600 mb-4">
            comet is a powerful Cloud Operations Management &amp; Engineering Tool designed to
            streamline your Azure infrastructure management and optimise cloud spending.
          </p>
          <img src="/dashboard.png" alt="comet Dashboard" className="w-full rounded-lg mb-4" />
          {!isAuthenticated && (
            <p className="text-sm text-teal-700 font-medium">
              Sign in with your Azure AD account to get started.
            </p>
          )}
          {isAuthenticated && (
            <Link
              to="/dashboard"
              className="inline-block bg-teal-500 hover:bg-teal-600 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Go to Dashboard →
            </Link>
          )}
        </section>

        <section className="bg-gray-50 p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-3">Key Benefits</h2>
          <ul className="space-y-3 mb-4">
            <li className="flex items-start gap-2">
              <span className="text-teal-500 mt-0.5">✓</span>
              <span className="text-gray-600">Centralised Azure resource price lookups</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-500 mt-0.5">✓</span>
              <span className="text-gray-600">Real-time pricing from the Azure Retail Prices API</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-500 mt-0.5">✓</span>
              <span className="text-gray-600">Multi-resource cost estimation with CSV export</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-500 mt-0.5">✓</span>
              <span className="text-gray-600">Side-by-side region price comparison</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-500 mt-0.5">✓</span>
              <span className="text-gray-600">Secure Azure AD authentication</span>
            </li>
          </ul>
          <img src="/benefits.png" alt="comet Benefits" className="w-full rounded-lg" />
        </section>

        <section className="bg-gray-50 p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-3">Features</h2>
          <div className="space-y-4 mb-4">
            <Feature icon="💰" title="Pricing Tool" description="Look up live Azure retail prices for Compute, Storage, Managed Disks, Public IPs, App Service, AKS, and SQL." />
            <Feature icon="🧮" title="Cost Estimator" description="Build multi-resource estimates and export to CSV for budget planning." />
            <Feature icon="⚖️" title="Price Comparison" description="Compare prices for the same SKU across multiple Azure regions." />
            <Feature icon="📂" title="Saved Estimates" description="Save and revisit cost estimates using your browser's local storage." />
            <Feature icon="🔒" title="Secure Access" description="Protected by Azure AD – only authorised users can access pricing data." />
          </div>
          <img src="/application.png" alt="comet Features" className="w-full rounded-lg" />
        </section>
      </div>
    </div>
  );
};

export default Home;
